import React, { useMemo, useState } from 'react';
import { Users, Filter, ArrowRight, Download, Info, Plus } from 'lucide-react';

// Audience Segmentation (Demo)
// - Curated segment cards with counts and % of total
// - Groups: Recency/Frequency, Value-based, Basket/Behavior, Category-based
// - Dummy data for 12-month horizon

const TOTAL = 1200;

function seedRand(seed){ let t=seed|0; return ()=>{ t^=t<<13; t^=t>>17; t^=t<<5; return Math.abs(t)/0x7fffffff; }; }

function useDummySegments(horizon = '12m') {
  const rnd = useMemo(()=>seedRand(horizon==='6m'? 24601 : 13579), [horizon]);
  return useMemo(() => {
    const pick = (min, max) => Math.max(min, Math.floor(min + rnd() * (max - min + 1)));
    const totals = TOTAL;
    const S = [];
    // Recency / Frequency
    S.push({ id:'rf_monthly_6', group:'Recency & Frequency', name:'Every Month (last 6 months)', count: pick(60,120) });
    S.push({ id:'rf_recent_3', group:'Recency & Frequency', name:'Ordered in last 3 months', count: pick(220,320) });
    S.push({ id:'rf_no_3', group:'Recency & Frequency', name:'No orders in last 3 months', count: pick(300,480) });
    S.push({ id:'rf_ytd10_no_last', group:'Recency & Frequency', name:'≥10 orders YTD, none last month', count: pick(40,110) });
    S.push({ id:'rf_first_30d', group:'Recency & Frequency', name:'First-time in last 30 days', count: pick(40,120) });
    S.push({ id:'rf_lapsed_6', group:'Recency & Frequency', name:'Lapsed ≥6 months', count: pick(120,220) });
    // Value-based
    S.push({ id:'val_gt_30k', group:'Value Based', name:'≥ ₺30K spent (12m)', count: pick(30,70) });
    S.push({ id:'val_gt_15k', group:'Value Based', name:'≥ ₺15K spent (12m)', count: pick(80,160) });
    S.push({ id:'val_top_aov', group:'Value Based', name:'Top 10% by AOV', count: pick(100,140) });
    S.push({ id:'val_high_margin', group:'Value Based', name:'High-margin purchasers', count: pick(90,160) });
    // Basket / Behavior
    S.push({ id:'beh_multi_item', group:'Basket & Behavior', name:'Avg ≥2 items / order', count: pick(260,420) });
    S.push({ id:'beh_bundle_lovers', group:'Basket & Behavior', name:'Bundle lovers (≥3 items often)', count: pick(90,160) });
    S.push({ id:'beh_fast_repeater', group:'Basket & Behavior', name:'Repeat within 30 days', count: pick(140,220) });
    S.push({ id:'beh_coupon_responders', group:'Basket & Behavior', name:'Coupon responders', count: pick(120,220) });
    S.push({ id:'beh_full_price', group:'Basket & Behavior', name:'Full‑price buyers', count: pick(90,160) });
    // Category Based (dummy categories)
    const cats = ['Sneakers','Beauty Care','Home & Living','Electronics','Accessories'];
    cats.forEach((c, i) => S.push({ id:`cat_${i}`, group:'Category Based', name:`${c} enthusiasts`, count: pick(80,180) }));
    // Deduplicate / bound counts
    return S.map(s => ({ ...s, pct: Math.round((s.count / totals) * 100) }));
  }, [rnd, horizon]);
}

const GROUP_META = {
  'Recency & Frequency': { gradient: 'from-indigo-500 to-blue-500' },
  'Value Based': { gradient: 'from-amber-500 to-orange-500' },
  'Basket & Behavior': { gradient: 'from-emerald-500 to-teal-500' },
  'Category Based': { gradient: 'from-fuchsia-500 to-purple-500' },
};

function SegCard({ seg, onPreview }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 hover:shadow-md transition">
      <div className="relative z-10">
        <div className="text-[11px] uppercase tracking-wide text-gray-500">{seg.group}</div>
        <div className="text-base font-semibold text-gray-900 mt-1">{seg.name}</div>
        <div className="mt-2 flex items-baseline gap-3">
          <div className="text-xl font-semibold text-gray-900">{seg.count.toLocaleString('en-US')}</div>
          <div className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{seg.pct}% of total</div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button onClick={() => onPreview?.(seg)} className="inline-flex items-center gap-1 text-xs text-gray-900 font-medium px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition">
            Preview audience <ArrowRight className="w-3 h-3" />
          </button>
          <button className="inline-flex items-center gap-1 text-xs text-white font-medium px-3 py-1.5 rounded-full bg-gray-900 hover:bg-black transition">
            Export <Download className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AudienceSegmentationDemo(){
  const [horizon, setHorizon] = useState('12m');
  const segments = useDummySegments(horizon);
  const [extraSegments, setExtraSegments] = useState([]);
  const [q, setQ] = useState('');
  const sourceSegments = useMemo(() => [...segments, ...extraSegments], [segments, extraSegments]);
  const filtered = useMemo(() => sourceSegments.filter(s =>
    s.name.toLowerCase().includes(q.toLowerCase()) || s.group.toLowerCase().includes(q.toLowerCase())
  ), [sourceSegments, q]);

  const groups = useMemo(() => {
    const m = {};
    filtered.forEach(s => { if(!m[s.group]) m[s.group] = []; m[s.group].push(s); });
    return m;
  }, [filtered]);

  const [showCreator, setShowCreator] = useState(false);
  const [form, setForm] = useState({
    name: '',
    group: 'Recency & Frequency',
    template: 'recent_n_months',
    n: 3,
    amount: 15000,
    percent: 5
  });

  const renderNameFromTemplate = (f) => {
    switch (f.template) {
      case 'recent_n_months': return `Ordered in last ${f.n} month${f.n>1?'s':''}`;
      case 'no_orders_n_months': return `No orders in last ${f.n} month${f.n>1?'s':''}`;
      case 'ytd_orders_gte': return `≥ ${f.n} orders YTD`;
      case 'value_gte': return `≥ ₺${Number(f.amount).toLocaleString('tr-TR')} spent (12m)`;
      case 'category_top': return `Top category: ${f.category || 'Selected'}`;
      default: return 'Custom segment';
    }
  };

  const handleCreate = () => {
    const name = form.name?.trim() || renderNameFromTemplate(form);
    const count = Math.max(1, Math.round(TOTAL * (Number(form.percent) / 100)));
    const seg = {
      id: `custom_${Date.now()}`,
      group: form.group,
      name,
      count,
      pct: Math.round((count / TOTAL) * 100)
    };
    setExtraSegments(prev => [seg, ...prev]);
    setShowCreator(false);
  };

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-2xl bg-gray-900 flex items-center justify-center shadow-md">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-gray-900">Audience Segmentation</h1>
              <p className="text-sm text-gray-500 mt-1">Curated segments for lifecycle, value, behavior, and category</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={()=>setShowCreator(true)} className="inline-flex items-center gap-2 text-sm font-medium text-white bg-gray-900 hover:bg-black px-3 py-2 rounded-lg">
              <Plus className="w-4 h-4" /> New segment
            </button>
            <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm p-2 border border-gray-200">
              <Filter className="w-4 h-4 text-gray-500" />
              <select value={horizon} onChange={(e)=>setHorizon(e.target.value)} className="text-sm outline-none bg-transparent">
                <option value="6m">Last 6 months</option>
                <option value="12m">Last 12 months</option>
              </select>
            </div>
            <input
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              placeholder="Search segments"
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-gray-400"
            />
          </div>
        </div>
      </div>

      {/* Create Segment Modal */}
      {showCreator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={()=>setShowCreator(false)} />
          <div className="relative z-10 w-full max-w-xl bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">Create new segment</h3>
              <button className="text-sm text-gray-500" onClick={()=>setShowCreator(false)}>Close</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-600 mb-1">Name</div>
                <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-gray-400"
                       value={form.name} onChange={e=>setForm({...form, name:e.target.value})}
                       placeholder={renderNameFromTemplate(form)} />
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Group</div>
                <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none"
                        value={form.group} onChange={e=>setForm({...form, group:e.target.value})}>
                  <option>Recency & Frequency</option>
                  <option>Value Based</option>
                  <option>Basket & Behavior</option>
                  <option>Category Based</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <div className="text-xs text-gray-600 mb-1">Template</div>
                <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none"
                        value={form.template} onChange={e=>setForm({...form, template:e.target.value})}>
                  <option value="recent_n_months">Recency: ordered in last N months</option>
                  <option value="no_orders_n_months">Recency: no orders in last N months</option>
                  <option value="ytd_orders_gte">Frequency: ≥ N orders YTD</option>
                  <option value="value_gte">Value: spent ≥ amount (12m)</option>
                  <option value="category_top">Category: top category</option>
                </select>
              </div>
              {(form.template === 'recent_n_months' || form.template === 'no_orders_n_months' || form.template === 'ytd_orders_gte') && (
                <div>
                  <div className="text-xs text-gray-600 mb-1">{form.template==='ytd_orders_gte' ? 'N (orders)' : 'N (months)'}</div>
                  <input type="number" min={1} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none"
                         value={form.n} onChange={e=>setForm({...form, n: Number(e.target.value)})} />
                </div>
              )}
              {form.template === 'value_gte' && (
                <div>
                  <div className="text-xs text-gray-600 mb-1">Amount (TRY)</div>
                  <input type="number" min={1000} step={500} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none"
                         value={form.amount} onChange={e=>setForm({...form, amount: Number(e.target.value)})} />
                </div>
              )}
              {form.template === 'category_top' && (
                <div>
                  <div className="text-xs text-gray-600 mb-1">Category</div>
                  <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none"
                         value={form.category||''} onChange={e=>setForm({...form, category:e.target.value})} placeholder="e.g. Sneakers" />
                </div>
              )}
              <div className="sm:col-span-2">
                <div className="text-xs text-gray-600 mb-1">Estimated size (% of total)</div>
                <div className="flex items-center gap-3">
                  <input type="range" min={1} max={50} value={form.percent} onChange={e=>setForm({...form, percent:Number(e.target.value)})} className="w-full" />
                  <div className="w-16 text-sm text-gray-900">{form.percent}%</div>
                </div>
                <div className="text-xs text-gray-500 mt-1">≈ {Math.round(TOTAL * form.percent / 100).toLocaleString('en-US')} users</div>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button className="text-sm px-3 py-2 rounded-lg border border-gray-200" onClick={()=>setShowCreator(false)}>Cancel</button>
              <button className="text-sm px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-black" onClick={handleCreate}>Create segment</button>
            </div>
          </div>
        </div>
      )}

      {/* Groups */}
      {Object.keys(groups).map((g) => (
        <div key={g} className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">{g}</h2>
            <div className="text-xs text-gray-500 flex items-center gap-1"><Info className="w-3.5 h-3.5" /> {groups[g].length} segments</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {groups[g].map(seg => (
              <SegCard key={seg.id} seg={seg} onPreview={() => { /* no-op demo */ }} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


